<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable"
                  :settings="{hideTable: true, hideExport: true, title: t('salesDetails'), appendQueryParams: {'version': '2'}, selectedPeriod: 'today', limitDateRangeTo30Days: true}"
                  :columns="columnDefs"
                  @onDataLoaded="onDataLoaded"
                  endpoint="reports/sales/total-sales">
      <template #filter>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="name"
            :placeholder="t('selectCustomer')"
            v-model="selectedCustomer"
            @change="onSelectedCustomerChanged"
            remote
            :remote-method="searchCustomer"
        >
          <el-option
              v-for="option in customers"
              :key="option.id"
              :label="option.name"
              :value="option"
          ></el-option>
        </el-select>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="fullName"
            :placeholder="t('selectEmployee')"
            v-model="selectedEmployee"
            @change="onSelectedEmployeeChanged"
            remote
            :remote-method="searchEmployees"
        >
          <el-option
              v-for="option in employees"
              :key="option.id"
              :label="option.fullName"
              :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #filter-bottom>
        <div class="flex">
          <el-select
              filterable
              clearable
              :placeholder="t('selectCityPlaceholder')"
              v-model="selectedCity"
              @change="onSelectedCityChanged"
              value-key="value"
              remote
              class="ms-auto !w-2/12"
              :remote-method="searchCities"
          >
            <el-option
                v-for="option in cities"
                :key="option.value"
                :label="option.value"
                :value="option.value"
            ></el-option>
          </el-select>
        </div>
      </template>
      <template #cards="slotProps">
        <div class="w-full text-xl font-bold mt-2">
          {{ t('totalSales') }}
        </div>
        <div class="grid grid-cols-3 gap-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('salesAmount') }}</div>
              <div class="text-2xl pt-2">{{
                  formatMoney(slotProps.data?.salesOverview?.sales_amount || 0, $ctx.currency)
                }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalOrders') }}</div>
              <div class="text-2xl pt-2">
                {{ slotProps.data?.salesOverview?.total_orders }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalItemsSold') }}</div>
              <div class="text-2xl pt-2">
                {{ slotProps.data?.salesOverview?.total_items_sold }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalCashIn') }}</div>
              <div class="text-2xl pt-2">{{
                  formatMoney(slotProps.data?.salesOverview?.total_cash_in || 0, $ctx.currency)
                }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{
                  t('totalTransactions')
                }}
              </div>
              <div class="text-2xl pt-2">
                {{ slotProps.data?.salesOverview?.total_transactions }}
              </div>
            </div>
          </el-card>
        </div>
      </template>
      <template #chart="slotProps">
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card"
               style="height: 400px">
            <v-chart :option="option" autoresize/>
          </div>
        </div>

        <el-table :data="slotProps.data?.orders || []" style="width: 100%"
                  default-expand-all
                  header-row-class-name="reports-table-header">
          <el-table-column type="expand">
            <template #default="props">
              <div class="ml-10">
                <el-table :data="props.row.orderItems"
                          header-row-class-name="reports-table-header2">
                  <el-table-column :label="t('#')" type="index"/>
                  <el-table-column :label="t('itemName')" width="250">
                    <template #default="props">
                        {{props.row.item?.name}}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('price')" width="150">
                    <template #default="props">
                        {{ formatMoney(props.row.itemSalePriceAmount || 0, 'IQD') }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('finalPrice')" width="150">
                    <template #default="props">
                        {{ formatMoney(props.row.soldAtPriceAmount || 0, 'IQD') }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('quantity')" width="150">
                    <template #default="props">
                      {{ props.row.quantity }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('total')" width="150">
                    <template #default="props">
                      {{ formatMoney((props.row.soldAtPriceAmount * props.row.quantity) || 0, 'IQD') }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="t('orderNumber')" prop="externalID" width="160">
            <template #default="props">
              <RouterLink :to="`/sales/orders/order/${props.row.id}`" class="font-bold">
                {{props.row.externalID}}
              </RouterLink>
            </template>
          </el-table-column>
          <el-table-column :label="t('total')" width="160">
            <template #default="props">
              <span class="text-blue-500 font-bold">
                {{ formatMoney(props.row.invoice?.totalDueAmount + props.row.invoice?.insuranceTotalDueAmount || 0, 'IQD') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="t('totalPaidAmount')" width="160">
            <template #default="props">
              <span class="text-blue-500 font-bold">
                {{ formatMoney(props.row.invoice?.totalPaidAmount || 0, 'IQD') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="t('customer')" width="160">
            <template #default="props">
              {{ props.row.invoice?.customer ? props.row.invoice?.customer.name : '' }}
            </template>
          </el-table-column>
          <el-table-column :label="t('cashier')">
            <template #default="props">
              {{ props.row?.user ? props.row?.user.name : '' }}
            </template>
          </el-table-column>
        </el-table>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, provide, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ColDef} from 'ag-grid-community'
import {useRoute, useRouter} from 'vue-router'
import {useHTTP} from '@/plugins/http'
import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
import {formatMoney} from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import User from '@/models/user/User'
import {use} from 'echarts/core'
import {CanvasRenderer} from 'echarts/renderers'
import {BarChart, PieChart} from 'echarts/charts'
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent
} from 'echarts/components'
import VChart, {THEME_KEY} from 'vue-echarts'
import Customer from "@/models/customer/Customer";
import {i18nOrderMessages} from "@/models/sales/Order";
import {useSettingsStore} from "@/stores/settingsStore";

interface Suggestion {
  value: string
  id?: string
}

interface SuggestionResult {
  value: string
  id?: string
}


// i18n
const {t} = useI18n({
  messages: {
    en: {
      ...i18nOrderMessages.en,
      salesDetails: 'Sales Details',
      totalSales: 'Total Sales',
      salesAmount: 'Sales Amount',
      totalOrders: 'Total Orders',
      totalItemsSold: 'Total Items Sold',
      totalCashIn: 'Total Cash In',
      totalTransactions: 'Total Transactions',
      credit: 'On Account',
      layaway: 'Layaway',
      direct: 'Full',
      cashOnDelivery: 'Cash on Delivery',
      finalPrice: "Final Price",
      selectEmployee: "Select Cashier",
      selectCustomer: "Select Customer",
      totalPaidAmount: "Total Paid Amount",
      selectCityPlaceholder: "Enter city"
    },
    ar: {
      ...i18nOrderMessages.ar,
      salesDetails: 'تفاصيل المبيعات',
      totalSales: 'اجمالي المبيعات',
      salesAmount: 'اجمالي المبيعات',
      totalOrders: 'اجمالي الطلبات',
      totalItemsSold: 'اجمالي عدد المبيوعات',
      totalCashIn: 'اجمالي الكاش',
      totalTransactions: 'اجمالي العمليات',
      credit: 'آجل',
      layaway: 'عربون',
      direct: 'كامل',
      cashOnDelivery: 'الدفع عن التوصيل',
      finalPrice: "السعر النهائي",
      selectEmployee: "اختر الموظف",
      selectCustomer: "اختر الزبون",
      totalPaidAmount: "اجمالي المدفوع",
      selectCityPlaceholder: "أدخل المدينة"
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()


use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')
const option = ref({
  title: {},
  grid: {
    top: 80,
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'item',
  },
  legend: {
    top: 30,
    orient: 'vertical',
    left: 'left',
    formatter: (data: any, x: any) => {
      return `${data}`
    }
  },
  series: [
    {
      type: 'pie',
      radius: '55%',
      center: ['50%', '60%'],
      data: [] as any[]
    }
  ]
})


const employees = ref<User[] | any>();
const selectedEmployee = ref<any>()


const customers = ref<Customer[] | any>();
const selectedCustomer = ref<any>()

const cities = ref<SuggestionResult[]>([])
const selectedCity = ref<string>('')

onBeforeMount(async () => {
  columnDefs.value = []
})


async function onSelectedEmployeeChanged(event: any) {
  rtable.value.payload.filter = selectedEmployee.value ? selectedEmployee.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchEmployees(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}?q=${query}`
    const employeesRes = await http.get<User[]>(url)
    employees.value = employeesRes.map((employee) => User.from(employee))
  } catch (error) {
    employees.value = []
  }
}


async function onSelectedCustomerChanged(event: any) {
  rtable.value.payload.customer_id = selectedCustomer.value ?
      selectedCustomer.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchCustomer(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Customer.ENDPOINT}?q=${query}`
    const customersRes = await http.get<Customer[]>(url)
    customers.value = customersRes.map((customer) => Customer.from(customer))
  } catch (error) {
    customers.value = []
  }
}

async function searchCities(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const response = await http.get<Suggestion[]>(`/helpers/suggestions?model=customers&key=city&query=${query}`)
    const result: SuggestionResult[] = response.map((r: Suggestion) => {
      return {
        value: r.value,
        id: r.id
      }
    })

    const settingsStore = useSettingsStore()
    settingsStore.cities.forEach(city => {
      if (!response.some((r: Suggestion) => r.value === city.arLabel)) {
        result.push({ value: city.arLabel } as SuggestionResult)
      }
    })

    cities.value = result
  } catch (e) {
    console.log(e)
    cities.value = []
  }
}

function onSelectedCityChanged() {
  rtable.value.payload.city = selectedCity.value ? selectedCity.value : null;
  rtable.value.navigate(rtable.value.payload)
}


function onDataLoaded(data: any) {
  console.log(data)
  const chartData: any = []
  chartData.push({
    value: data.salesOverview.total_direct,
    name: t('direct')
  });

  chartData.push({
    value: data.salesOverview.total_layaway,
    name: t('layaway')
  });

  chartData.push({
    value: data.salesOverview.total_credit,
    name: t('credit')
  });

  chartData.push({
    value: data.salesOverview.total_cash_on_delivery,
    name: t('cashOnDelivery')
  });

  option.value.series[0].data = chartData
}
</script>
